import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Box, Button, Tooltip } from "@mui/material";
import { MRT_RowSelectionState } from "material-react-table";

import { TrashIcon } from "assets";
import { Bill } from "api";
import {
  useBulkDeleteUtilityBills,
  useDocumentBills,
  useGetConfigParameter,
  useNotify,
  useUtilityBillById,
} from "hooks";
import { useSaveCurrentUrl, useUserRole } from "recoils";
import {
  getFromLocalStorage,
  setToLocalStorage,
  isFeatureEnabled,
} from "utils";
import {
  CustomMaterialTable,
  DeleteDialog,
  Label12Regular,
  Label16SemiBold,
  Loading,
  useDialogControls,
} from "components";

import { UTILITY_BILL_PAGE } from "pages/authenticated/documents/document-summary-v2/route-consts";

import { DOCUMENT_UTILITY_BILLS_COLUMNS } from "./columns";
import { DOCUMENT_UPLOAD_PAGE, ADD_DOCUMENT_BILL_PAGE } from "../../consts";

import { DocumentExpandedRowProps } from "./types";

import { BulkActionsBar } from "./components";

export const DocumentExpandedRow: React.FunctionComponent<
  DocumentExpandedRowProps
> = ({ row }) => {
  const notify = useNotify();
  const deleteDialogControls = useDialogControls();
  const saveCurrentUrl = useSaveCurrentUrl();
  const navigate = useNavigate();
  const { deleteBulkBills } = useBulkDeleteUtilityBills();
  const dataEntriesFromStorage = getFromLocalStorage("dataEntries") || "";
  const dataEntries = dataEntriesFromStorage
    ? JSON.parse(dataEntriesFromStorage)?.original?.bills
    : "";

  const documentId = window.sessionStorage.getItem("summaryDocumentID");

  const document = getFromLocalStorage("document") || "";
  const documentData = (document && JSON.parse(document)) || "";
  const addEntriesDelete = getFromLocalStorage("billEntriesIdSelected");
  const clickedDataEntryId = getFromLocalStorage("dataEntryIdSelected");
  const [rowSelection, setRowSelection] = useState<MRT_RowSelectionState>({});
  const rowSelectionLength = Object.keys(rowSelection).length;
  const selectedBillIds = Object.keys(rowSelection);
  const { billId } = useParams<{ [UTILITY_BILL_PAGE.KEY]: string }>();
  const currentEntryId = billId || clickedDataEntryId;
  const { data: utilityBill } = useUtilityBillById(billId || "");
  const documentGUID = utilityBill?.documentGuid;
  const { data, isLoading } = useDocumentBills(
    documentGUID || documentId || "",
  );
  const userRole = useUserRole();
  const { data: configParameter } = useGetConfigParameter();
  const showReviewed = isFeatureEnabled(
    configParameter,
    "second_layer_approval_feature",
  );
  const showNotApplicable = isFeatureEnabled(
    configParameter,
    "unusable_status_enabled",
  );
  const currentURL = new URL(window.location.href).pathname;

  const billsLength = data?.[0]?.bills?.length;

  const onDeleteClick = () => {
    localStorage.removeItem("document");
    const selectedIds = Object.keys(rowSelection);
    const idsToDelete =
      selectedIds.length >= 1
        ? selectedIds
        : addEntriesDelete
        ? [addEntriesDelete]
        : [];

    if (idsToDelete.length === 0) {
      deleteDialogControls.close();
      return;
    }

    deleteBulkBills({ ids: idsToDelete })
      .then(() => {
        localStorage.removeItem("billEntriesIdSelected");
        setRowSelection({} as MRT_RowSelectionState);
        if (billsLength !== undefined && idsToDelete.length >= billsLength) {
          navigate(DOCUMENT_UPLOAD_PAGE.ABSOLUTE_PATH);
        }
      })
      .catch(() => {
        // Toasts for both request-level and partial-item failures are
        // already shown by useBulkDeleteUtilityBills — nothing else to do here.
      })
      .finally(() => {
        deleteDialogControls.close();
      });
  };

  const handleRowClick = async (rowData: any) => {
    const currentId = rowData?.id || rowData?.original?.id;

    if (currentId === undefined || currentId === null) {
      notify.error(
        "Manual data entries are disabled for system-generated bills.",
      );
      return;
    }

    saveCurrentUrl();
    navigate(ADD_DOCUMENT_BILL_PAGE.ABSOLUTE_PATH(currentId));
  };

  const handleDataEntryClick = (dataEntry: any) => {
    const dataEntryToStore = dataEntry?.row?.id || dataEntry?.row?.original?.id;

    if (dataEntryToStore) {
      setToLocalStorage("dataEntryIdSelected", dataEntryToStore);

      navigate(`${UTILITY_BILL_PAGE.ABSOLUTE_PATH(dataEntryToStore)}`);
    }
  };

  const getMuiTableBodyRowProps = (dataEntry: any) => ({
    onClick: () => handleDataEntryClick(dataEntry),
    sx: {
      backgroundColor:
        String(dataEntry?.row?.original?.id) === String(currentEntryId)
          ? "#DCDCDC"
          : "",
      cursor: "pointer",
    },
  });

  useEffect(() => {
    if (documentData?.filename !== undefined) {
      if (
        row &&
        row.original &&
        row.original.fileName !== documentData.filename
      ) {
        localStorage.removeItem("document");
      }
    }
  }, [currentURL, documentData?.filename, row, row.original]);

  const displayedFileName =
    document === ""
      ? row && row.original && row.original.fileName
      : documentData?.filename;

  return (
    <Box>
      <Box
        mb={3}
        display="flex"
        alignItems="center"
        justifyContent="space-between"
      >
        <Box sx={{ minWidth: 0, overflow: "hidden", mr: 2, gap: "12px" }}>
          <Tooltip title={displayedFileName} placement="top">
            <Label16SemiBold mb={0.5} noWrap>
              {displayedFileName}
            </Label16SemiBold>
          </Tooltip>
          <Label12Regular>Billed service(s)</Label12Regular>
        </Box>
        {(!(dataEntries?.length === 0) || documentId !== "") && (
          <Box>
            <Button
              startIcon={<TrashIcon />}
              variant="outlined"
              disabled={rowSelectionLength === 0 || userRole.isAuditor}
              onClick={deleteDialogControls.open}
            >
              Delete
            </Button>
            <Button
              sx={{ ml: 2 }}
              variant="outlined"
              disabled={userRole.isAuditor}
              onClick={() => handleRowClick(row)}
            >
              Add data entry
            </Button>
          </Box>
        )}
      </Box>
      {!isLoading && rowSelectionLength > 0 && (
        <BulkActionsBar
          selectedBillIds={selectedBillIds}
          selectedCount={rowSelectionLength}
          disabled={userRole.isAuditor}
          onClear={() => setRowSelection({} as MRT_RowSelectionState)}
          showReviewed={showReviewed}
          showNotApplicable={showNotApplicable}
        />
      )}
      {isLoading ? (
        <Box
          sx={{
            display: "flex",
            height: "300px",
            position: "relative",
            padding: "24px 41px",
          }}
        >
          <Loading />
        </Box>
      ) : (
        <CustomMaterialTable
          data={data?.[0]?.bills ?? []}
          columns={DOCUMENT_UTILITY_BILLS_COLUMNS}
          enablePagination={false}
          enableColumnResizing
          enableRowSelection
          onRowSelectionChange={setRowSelection}
          state={{ rowSelection }}
          getRowId={(bill: Bill) => bill.id}
          muiTableBodyRowProps={(billRow: any) =>
            getMuiTableBodyRowProps(billRow)
          }
        />
      )}
      <DeleteDialog
        isOpen={deleteDialogControls.isOpen}
        onCancel={deleteDialogControls.close}
        onDeleteClick={onDeleteClick}
        selectedCount={rowSelectionLength ?? 0}
        target="bill"
        title={`Delete this data entry in document: ${
          document === ""
            ? row && row.original && row.original.fileName
            : documentData.filename
        }`}
      />
    </Box>
  );
};
